import { NavLink, useLocation } from 'react-router-dom'
import { ChevronDown } from 'lucide-react'
import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth'
import { api } from '@/lib/api'
import { getNav, filtrarPorPermissoes, type NavItem } from './Sidebar'

// Menu horizontal "prime" — barra no topo com dropdowns. Reusa getNav (mesma
// lógica de módulos do sidebar). Aparece só no desktop; no mobile o Layout
// continua usando o drawer lateral.
export default function TopMenu() {
  const { user, empresa } = useAuth()
  const loc = useLocation()
  const isSuperAdmin = user?.perfil === 'super_admin'
  const isAdmin = isSuperAdmin || user?.nivel === 'admin' || user?.perfil === 'admin'
  const industrialAtivo = (empresa?.modulos_ativos as any)?.industrial !== false

  const { data: opContagem } = useQuery<Record<string, number>>({
    queryKey: ['ops-contagem'], queryFn: async () => (await api.get('/industrial/ordens-contagem')).data,
    enabled: !!user && !isSuperAdmin && industrialAtivo, refetchInterval: 30000,
  })
  const { data: qualidade } = useQuery<{ pendentes: number }>({
    queryKey: ['qualidade-decisoes-contagem'], queryFn: async () => (await api.get('/vendas/qualidade/contagem')).data,
    enabled: !!user && !isSuperAdmin, refetchInterval: 30000,
  })
  const { data: credito } = useQuery<{ pendentes: number }>({
    queryKey: ['credito-pendentes-contagem'], queryFn: async () => (await api.get('/financeiro/analise-credito/contagem-pendentes')).data,
    enabled: !!user && !isSuperAdmin, refetchInterval: 60000,
  })

  const NAV_FULL = getNav(isSuperAdmin, empresa?.modulos_ativos ?? null,
    opContagem?.proposta || 0, empresa?.id ?? null,
    qualidade?.pendentes || 0, credito?.pendentes || 0)
  const NAV = filtrarPorPermissoes(NAV_FULL, user?.permissoes, isAdmin)

  const ativo = (it: NavItem) =>
    loc.pathname === it.to ||
    (it.to !== '/' && loc.pathname.startsWith(it.to)) ||
    (it.children || []).some(c => loc.pathname === c.to)

  return (
    <nav className="hidden lg:flex flex-wrap items-center gap-x-0.5 gap-y-1">
      {NAV.filter(it => !it.adminOnly || isAdmin).map(it => {
        const Icon = it.icon
        const on = ativo(it)
        const temFilhos = !!(it.children && it.children.length)
        const base = `relative flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-colors ${
          on ? 'bg-brand-600 text-white shadow-sm' : 'text-ink-600 hover:bg-ink-100 hover:text-ink-900'
        }`
        if (!temFilhos) {
          return (
            <NavLink key={it.to} to={it.to} className={base}>
              {Icon && <Icon size={15} />}<span>{it.label}</span>
              {it.badge && <span className="text-[9px] font-bold px-1 rounded bg-ink-200 text-ink-700">{it.badge}</span>}
              {!!it.badgeCount && <span className="text-[9px] font-bold px-1.5 rounded-full bg-red-500 text-white">{it.badgeCount}</span>}
            </NavLink>
          )
        }
        return (
          <div key={it.to} className="relative group">
            <button className={base}>
              {Icon && <Icon size={15} />}<span>{it.label}</span>
              {!!it.badgeCount && <span className="text-[9px] font-bold px-1.5 rounded-full bg-red-500 text-white">{it.badgeCount}</span>}
              <ChevronDown size={13} className="opacity-60" />
            </button>
            <div className="absolute left-0 top-full pt-1 hidden group-hover:block z-50 min-w-[210px]">
              <div className="bg-white border border-ink-200 rounded-xl shadow-xl py-1.5 overflow-hidden">
                <NavLink to={it.to}
                  className={({ isActive }) => `block px-3.5 py-2 text-sm ${isActive ? 'text-brand-700 font-semibold bg-brand-50' : 'text-ink-700 hover:bg-ink-50'}`}>
                  {it.label} — visão geral
                </NavLink>
                <div className="h-px bg-ink-100 my-1" />
                {it.children!.map(c => (
                  <NavLink key={c.to} to={c.to}
                    className={({ isActive }) => `flex items-center justify-between px-3.5 py-2 text-sm ${isActive ? 'text-brand-700 font-semibold bg-brand-50' : 'text-ink-700 hover:bg-ink-50'}`}>
                    <span>{c.label}</span>
                    {!!c.badgeCount && <span className="text-[9px] font-bold px-1.5 rounded-full bg-red-500 text-white">{c.badgeCount}</span>}
                  </NavLink>
                ))}
              </div>
            </div>
          </div>
        )
      })}
    </nav>
  )
}
